import { FormEvent, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { createRma } from '@/lib/api/inventory-rmas.js';
import { listQuarantineItems } from '@/lib/api/warranty.js';
import { SupplierSelect } from '@/components/suppliers/SupplierSelect.js';
import { formatCurrency, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function RmaCreatePage() {
  const navigate = useNavigate();
  const [supplierId, setSupplierId] = useState('');
  const [reason, setReason] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const itemsQuery = useQuery({
    queryKey: ['quarantine-items', supplierId],
    queryFn: () => listQuarantineItems(supplierId),
    enabled: Boolean(supplierId),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createRma({
        supplierId,
        resolutionType: 'credit',
        reason: reason || undefined,
        partFailureIds: selected,
      }),
    onSuccess: (rma) => {
      toastSuccess(`Created ${rma.rmaNumber}`);
      navigate(`/inventory/rmas/${rma.id}`);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function toggle(id: string) {
    setSelected((current) => (current.includes(id) ? current.filter((row) => row !== id) : [...current, id]));
  }

  const rows = itemsQuery.data ?? [];
  const selectedCost = rows
    .filter((row) => selected.includes(row.id))
    .reduce((sum, row) => sum + Number(row.unitCost || 0), 0);

  return (
    <form
      className="mx-auto max-w-3xl space-y-5"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        createMutation.mutate();
      }}
    >
      <div>
        <Link to="/inventory/rmas" className="text-sm font-medium text-slate-500 hover:text-slate-800">
          ← Back to RMAs
        </Link>
        <h1 className="mt-2 text-xl font-bold text-slate-950">New supplier RMA</h1>
        <p className="text-sm text-slate-500">
          Pick a supplier, then add the quarantine items waiting for that supplier.
        </p>
      </div>

      <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <label className="block">
          <span className="text-sm font-medium">Supplier</span>
          <div className="mt-1">
            <SupplierSelect
              value={supplierId}
              onChange={(id) => {
                setSupplierId(id);
                setSelected([]);
              }}
              required
            />
          </div>
        </label>
        <label className="block">
          <span className="text-sm font-medium">Reason</span>
          <textarea
            className="mt-1 w-full rounded-xl border px-3 py-2"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <h2 className="font-semibold text-slate-950">Quarantine for this supplier</h2>
          <span className="text-sm text-slate-500">{formatCurrency(selectedCost)} selected</span>
        </div>
        {!supplierId ? (
          <p className="px-5 py-8 text-sm text-slate-500">Choose a supplier to see parts waiting to ship.</p>
        ) : itemsQuery.isLoading ? (
          <p className="px-5 py-8 text-sm text-slate-500">Loading quarantine…</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">Nothing in quarantine for this supplier.</p>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-5 py-3" />
                <th className="px-5 py-3">Part</th>
                <th className="px-5 py-3">Failure</th>
                <th className="px-5 py-3">Days</th>
                <th className="px-5 py-3">Cost</th>
                <th className="px-5 py-3">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="px-5 py-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(row.id)}
                      onChange={() => toggle(row.id)}
                    />
                  </td>
                  <td className="px-5 py-3 font-medium">
                    {row.partName}
                    {row.inventorySerialId ? <span className="ml-2 text-xs text-slate-500">Serial</span> : null}
                  </td>
                  <td className="px-5 py-3">{prettyLabel(row.failureType)}</td>
                  <td className="px-5 py-3">{row.daysToFailure ?? '—'}</td>
                  <td className="px-5 py-3">{formatCurrency(row.unitCost)}</td>
                  <td className="px-5 py-3">{row.sourceTicketNumber ?? 'Stock'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <button
        type="submit"
        disabled={!supplierId || selected.length === 0 || createMutation.isPending}
        className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
      >
        {createMutation.isPending ? 'Creating…' : `Create RMA (${selected.length})`}
      </button>
    </form>
  );
}
